/** Measures the perceived loudness of a MediaStream and tracks whether it stayed too quiet. */
export class AudioLevelMeter {
  private context: AudioContext | undefined;
  private analyser: AnalyserNode | undefined;
  private source: MediaStreamAudioSourceNode | undefined;
  private samples = new Float32Array(0);

  private current = 0;
  private peak = 0;

  /** Loudness quieter than this (dBFS) reads as silence. */
  private readonly FLOOR_DB = -60;
  /** Loudness at or above this (dBFS) reads as full scale. */
  private readonly CEIL_DB = -10;
  /** Scales raw RMS into the range the quiet threshold is expressed in. */
  private readonly PEAK_BOOST = 5;
  /** A peak below this is treated as background static rather than speech. */
  private readonly QUIET_THRESHOLD = 0.05;

  /** Begin measuring the given stream, replacing any stream already in progress. */
  public start(stream: MediaStream) {
    this.stop();
    this.context = new AudioContext();
    this.analyser = new AnalyserNode(this.context, { fftSize: 512 });
    this.source = new MediaStreamAudioSourceNode(this.context, { mediaStream: stream });
    this.source.connect(this.analyser);
    this.samples = new Float32Array(this.analyser.fftSize);
    void this.resume();
  }

  /** Resume the context, which Chrome suspends until a user gesture. Call from within a gesture. */
  public async resume() {
    if (this.context?.state === 'suspended') {
      await this.context.resume();
    }
  }

  /** Read the microphone once, refreshing the current level and observed peak. Call once per frame. */
  public sample() {
    if (!this.analyser) {
      this.current = 0;
      return;
    }
    this.analyser.getFloatTimeDomainData(this.samples);
    let sumSquares = 0;
    for (const amplitude of this.samples) {
      sumSquares += amplitude * amplitude;
    }
    const rms = Math.sqrt(sumSquares / this.samples.length);
    this.peak = Math.max(this.peak, rms * this.PEAK_BOOST);
    const db = rms > 0 ? 20 * Math.log10(rms) : this.FLOOR_DB;
    const norm = (db - this.FLOOR_DB) / (this.CEIL_DB - this.FLOOR_DB);
    this.current = Math.min(Math.max(norm, 0), 1);
  }

  /** Perceived loudness from 0 to 1 as of the most recent sample(). */
  public level() {
    return this.current;
  }

  /** Forget the observed peak, e.g. at the start of a new recording. */
  public resetPeak() {
    this.peak = 0;
  }

  /** Whether the loudest moment since the last reset was basically background static. */
  public isTooQuiet() {
    return this.peak < this.QUIET_THRESHOLD;
  }

  /** Tear down the audio graph and free the microphone-connected resources. */
  public stop() {
    this.source?.disconnect();
    void this.context?.close();
    this.context = undefined;
    this.analyser = undefined;
    this.source = undefined;
  }
}
